import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../hooks/useTheme";
import AppSidebar from "./AppSidebar";
import MetaHead from "./MetaHead";
import { useTranslation } from "react-i18next";
import {
  FormInput,
  FormTextarea,
  FormSelect,
  FormButton,
} from "./FormComponents";
import TrackerInfo from "./TrackerInfo";
import DataTypeExamples from "./DataTypeExamples";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { createTracker } from "../store/slices/trackersSlice";
import { trackPageVisit, trackUserInteraction } from "../utils/analytics";
import { DayOfWeek } from "../types";
import { useToastContext } from "./ToastContext";

const AddTracker: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { addToast } = useToastContext();
  const userProfile = useAppSelector((state) => state.user.profile);
  const { isDark, setTheme } = useTheme();
  const initials = userProfile?.name
    ? userProfile.name
        .split(" ")
        .map((w: string) => w.charAt(0).toUpperCase())
        .slice(0, 2)
        .join("")
    : (userProfile?.username?.charAt(0).toUpperCase() ?? "U");

  const handleThemeToggle = () => {
    setTheme(isDark ? "light" : "dark");
  };

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    dataType: "" as "number" | "string" | "boolean" | "time" | "options" | "",
    unit: "",
    options: [] as string[],
    trackingDays: [] as DayOfWeek[],
  });

  const dataTypeOptions = t("addTracker.dataTypeOptions", {
    returnObjects: true,
  }) as { value: string; label: string }[];

  useEffect(() => {
    trackPageVisit("add_tracker");
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.dataType) {
      return;
    }

    // Validate options for 'options' dataType
    if (
      formData.dataType === "options" &&
      (!formData.options ||
        formData.options.length === 0 ||
        formData.options.some((opt) => !opt.trim()))
    ) {
      return;
    }

    setLoading(true);

    try {
      // Dispatch createTracker action to Redux store
      const result = await dispatch(
        createTracker({
          name: formData.name.trim(),
          dataType: formData.dataType,
          description: formData.description.trim() || undefined,
          unit: formData.unit.trim() || undefined,
          options:
            formData.dataType === "options"
              ? formData.options.filter((opt) => opt.trim())
              : undefined,
          trackingDays:
            formData.trackingDays.length > 0
              ? formData.trackingDays
              : undefined,
          isActive: true,
          isPaused: false,
        }),
      ).unwrap();

      // Show toast if points earned
      if (result.pointsEarned > 0) {
        addToast(
          t("toast.pointsEarned", { points: result.pointsEarned }),
          "success",
        );
      }

      // Track tracker creation event
      trackUserInteraction(
        "create_tracker",
        "tracker_management",
        result?.id || "unknown",
      );

      // Navigate to trackers page
      navigate("/trackers");
    } catch (error) {
      console.error("Error saving tracker:", error);
      // TODO: Show error message to user
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/trackers");
  };

  const showUnitField = formData.dataType === "number";

  return (
    <>
      <MetaHead
        title={t("addTracker.title")}
        description={t("addTracker.description") as string}
        keywords={t("addTracker.keywords") as string}
      />
      <div className="min-h-screen bg-[#eeede9] dark:bg-[#080809] flex flex-col lg:flex-row lg:max-w-6xl lg:mx-auto">
        {/* DESKTOP SIDEBAR */}
        <AppSidebar showQuickActions={false} />

        {/* MAIN CONTENT */}
        <main className="flex-1 flex flex-col bg-[#f7f6f3] dark:bg-[#0f0f12] min-h-screen lg:min-h-0 pb-24 lg:pb-8">
          {/* MOBILE TOPBAR */}
          <header className="lg:hidden flex items-center justify-between px-4 py-3.5 border-b border-black/[0.07] dark:border-white/[0.07] sticky top-0 bg-[#f7f6f3] dark:bg-[#0f0f12] z-20">
            <button
              onClick={handleCancel}
              aria-label="Go back"
              className="tap-scale w-8 h-8 rounded-[9px] bg-[#3b5cde]/20 border border-[#3b5cde]/30 flex items-center justify-center transition-all hover:bg-[#3b5cde]/30"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#7b9fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <p className="text-[21px] font-semibold tracking-tight text-gray-900 dark:text-white leading-none">
              {t("addTracker.headerTitle")}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleThemeToggle}
                aria-label={
                  isDark
                    ? (t("theme.switchToLight") as string)
                    : (t("theme.switchToDark") as string)
                }
                className="w-8 h-8 rounded-[9px] border border-black/10 dark:border-white/10 bg-black/[0.04] dark:bg-white/[0.05] text-gray-500 dark:text-white/50 text-[19px] flex items-center justify-center cursor-pointer transition-all hover:bg-black/10 dark:hover:bg-white/10"
              >
                {isDark ? "☀️" : "🌙"}
              </button>
              <button
                onClick={() => navigate("/profile")}
                aria-label="Go to profile"
                className="w-8 h-8 rounded-full bg-[#3b5cde] flex items-center justify-center text-[16px] font-semibold text-white"
              >
                {initials}
              </button>
            </div>
          </header>

          {/* DESKTOP PAGE HEADER */}
          <div className="hidden lg:flex items-center gap-4 px-6 py-5 border-b border-black/[0.07] dark:border-white/[0.07]">
            <button
              onClick={handleCancel}
              aria-label="Go back"
              className="tap-scale w-9 h-9 rounded-xl bg-[#3b5cde]/15 border border-[#3b5cde]/25 flex items-center justify-center transition-all hover:bg-[#3b5cde]/25"
            >
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 3L5 8l5 5"
                  stroke="#7b9fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <h1 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
              {t("addTracker.headerTitle")}
            </h1>
          </div>

          {/* SCROLLABLE CONTENT */}
          <div className="flex-1 overflow-y-auto scrollbar-hide px-3 lg:px-6 py-4">
            {/* Form Content */}
            <div className="max-w-3xl mx-auto pb-8 space-y-4">
              <div className="rounded-xl border border-[#a33fe8]/20 dark:border-[#a33fe8]/30 bg-gradient-to-br from-[#a33fe8]/[0.07] to-transparent dark:from-[#a33fe8]/[0.09] p-4 lg:p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#a33fe8]/20 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-[24px] leading-none">📊</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-[19px] font-semibold text-gray-900 dark:text-white mb-1">
                      {t("addTracker.headerTitle")}
                    </p>
                    <p className="text-[16px] text-gray-600 dark:text-white/45 leading-relaxed">
                      {t("addTracker.helpText")}
                    </p>
                  </div>
                </div>
              </div>

              <div className="">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/[0.1] rounded-xl p-4">
                      <FormInput
                        id="name"
                        label={t("addTracker.trackerName") as string}
                        value={formData.name}
                        onChange={(value) =>
                          setFormData({ ...formData, name: value })
                        }
                        placeholder={
                          t("addTracker.trackerNamePlaceholder") as string
                        }
                        required
                      />
                    </div>

                    <div className="bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/[0.1] rounded-xl p-4">
                      <FormSelect
                        id="dataType"
                        label={t("addTracker.dataType") as string}
                        value={formData.dataType}
                        onChange={(value) =>
                          setFormData({
                            ...formData,
                            dataType: value as
                              | "number"
                              | "string"
                              | "boolean"
                              | "time"
                              | "options",
                          })
                        }
                        options={dataTypeOptions}
                        placeholder={
                          t("addTracker.dataTypePlaceholder") as string
                        }
                        required
                      />
                    </div>
                  </div>

                  {showUnitField && (
                    <div className="bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/[0.1] rounded-xl p-4">
                      <FormInput
                        id="unit"
                        label={t("addTracker.unit") as string}
                        value={formData.unit}
                        onChange={(value) =>
                          setFormData({ ...formData, unit: value })
                        }
                        placeholder={t("addTracker.unitPlaceholder") as string}
                      />
                    </div>
                  )}

                  {formData.dataType === "options" && (
                    <div className="bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/[0.1] rounded-xl p-4">
                      <label className="block text-[17px] font-semibold text-gray-900 dark:text-white/80 mb-2">
                        {t("addTracker.options")}
                        <span className="text-red-500 ml-1">*</span>
                      </label>
                      <div className="space-y-2">
                        {formData.options.map((option, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <input
                              type="text"
                              value={option}
                              onChange={(e) => {
                                const newOptions = [...formData.options];
                                newOptions[index] = e.target.value;
                                setFormData({
                                  ...formData,
                                  options: newOptions,
                                });
                              }}
                              placeholder={
                                t("addTracker.optionPlaceholder", {
                                  index: index + 1,
                                }) as string
                              }
                              className="flex-1 px-4 py-3 rounded-xl border border-black/[0.1] dark:border-white/[0.1] bg-black/[0.04] dark:bg-white/[0.06] placeholder:text-gray-400 dark:placeholder:text-white/25 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:border-[#a33fe8] transition-colors"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newOptions = formData.options.filter(
                                  (_, i) => i !== index,
                                );
                                setFormData({
                                  ...formData,
                                  options: newOptions,
                                });
                              }}
                              className="p-2 rounded-lg border border-red-500/20 text-red-500 hover:text-red-600 hover:bg-red-500/10 transition-all"
                            >
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() =>
                            setFormData({
                              ...formData,
                              options: [...formData.options, ""],
                            })
                          }
                          className="inline-flex items-center gap-2 px-3 py-2 text-[17px] font-medium rounded-lg border border-[#a33fe8]/30 text-[#a33fe8] bg-[#a33fe8]/10 hover:bg-[#a33fe8]/15 transition-all"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                            />
                          </svg>
                          <span>{t("addTracker.addOption")}</span>
                        </button>
                      </div>
                      <p className="text-[15px] text-gray-500 dark:text-white/40 mt-2">
                        {t("addTracker.optionsHelpText")}
                      </p>
                    </div>
                  )}

                  <div className="bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/[0.1] rounded-xl p-4">
                    <FormTextarea
                      id="description"
                      label={t("addTracker.descriptionLabel") as string}
                      value={formData.description}
                      onChange={(value) =>
                        setFormData({ ...formData, description: value })
                      }
                      placeholder={
                        t("addTracker.descriptionPlaceholder") as string
                      }
                      rows={3}
                    />
                  </div>

                  {/* Tracking Days */}
                  <div className="bg-black/[0.03] dark:bg-white/[0.04] border border-black/[0.08] dark:border-white/[0.1] rounded-xl p-4">
                    <label className="block text-[17px] font-semibold text-gray-900 dark:text-white/80 mb-3">
                      {t("addTracker.trackingDays")}
                      <span className="text-gray-500 dark:text-white/40 ml-2 font-medium">
                        {t("addTracker.trackingDaysOptional")}
                      </span>
                    </label>
                    <div className="grid grid-cols-2 gap-2.5">
                      {(
                        [
                          "monday",
                          "tuesday",
                          "wednesday",
                          "thursday",
                          "friday",
                          "saturday",
                          "sunday",
                        ] as DayOfWeek[]
                      ).map((day) => (
                        <label
                          key={day}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-black/[0.08] dark:border-white/[0.1] bg-white/[0.65] dark:bg-white/[0.03] hover:bg-black/[0.03] dark:hover:bg-white/[0.06] transition-all cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData.trackingDays.includes(day)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  trackingDays: [...formData.trackingDays, day],
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  trackingDays: formData.trackingDays.filter(
                                    (d) => d !== day,
                                  ),
                                });
                              }
                            }}
                            className="w-4 h-4 rounded border-black/20 dark:border-white/20 text-[#a33fe8] focus:ring-[#a33fe8]/40"
                          />
                          <span className="text-[17px] font-medium text-gray-900 dark:text-white/85 capitalize">
                            {day}
                          </span>
                        </label>
                      ))}
                    </div>
                    <p className="text-[15px] text-gray-500 dark:text-white/40 mt-2">
                      {t("addTracker.trackingDaysHelpText")}
                    </p>
                  </div>

                  {/* Tracker Info */}
                  <TrackerInfo />

                  {/* Examples based on selected type */}
                  <DataTypeExamples dataType={formData.dataType} />

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-black/[0.07] dark:border-white/[0.08]">
                    <div className="">
                      <FormButton
                        type="submit"
                        variant="primary"
                        loading={loading}
                        disabled={
                          !formData.name.trim() ||
                          !formData.dataType ||
                          (formData.dataType === "options" &&
                            (!formData.options ||
                              formData.options.length === 0 ||
                              formData.options.some((opt) => !opt.trim())))
                        }
                        size="lg"
                      >
                        {loading
                          ? t("addTracker.creating")
                          : t("addTracker.createTracker")}
                      </FormButton>
                    </div>
                    <div className="">
                      <FormButton
                        type="button"
                        variant="secondary"
                        onClick={handleCancel}
                        disabled={loading}
                        size="lg"
                      >
                        {t("addTracker.cancel")}
                      </FormButton>
                    </div>
                  </div>
                </form>
              </div>

              {/* Help Text */}
              <div className="">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-black/[0.07] dark:border-white/[0.1] bg-white/[0.7] dark:bg-white/[0.05]">
                  <svg
                    className="w-4 h-4 text-[#a33fe8]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-[16px] text-gray-600 dark:text-white/45 font-medium">
                    {t("addTracker.helpText")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default AddTracker;
